import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, setActiveBusiness } from './api';
import { clearPushOnLogout } from './push';
import { useNavigate } from 'react-router-dom';

export interface SubscriptionView {
  id: string;
  planKey: string;
  planName: string;
  billingCycle: string;
  status: 'trialing' | 'active' | 'expired' | 'past_due' | 'canceled' | 'suspended';
  trialStartedAt: string | null;
  trialEndsAt: string | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  provider: string;
  maxProfessionals: number;
}

export interface Membership {
  businessId: string;
  businessName: string;
  slug: string;
  professionalSlug: string | null;
  businessStatus: string;
  onboardingStep: number;
  businessTheme: string | null;
  businessLogo: string | null;
  role: 'owner' | 'manager' | 'professional';
  permissions: string[];
  professionalId: string | null;
  subscription: SubscriptionView | null;
}

export interface CurrentUser {
  id: string;
  email: string;
  fullName: string;
  phone?: string | null;
  isPlatformAdmin?: boolean;
}

interface MeResponse {
  user: CurrentUser;
  memberships: Membership[];
}

interface AuthValue {
  loading: boolean;
  user: CurrentUser | null;
  memberships: Membership[];
  active: Membership | null;
  isAdmin: boolean;
  refresh: () => Promise<void>;
  login: (email: string, password: string, remember?: boolean, portal?: 'empresa' | 'admin') => Promise<CurrentUser>;
  logout: () => Promise<void>;
  selectBusiness: (businessId: string) => void;
}

const AuthContext = createContext<AuthValue | null>(null);
const BUSINESS_KEY = 'nailsly.activeBusiness';
// Apenas o último e-mail lembrado (não é credencial): serve para pré-preencher o login.
const REMEMBER_KEY = 'nailsly.rememberedEmail';

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [activeBusinessId, setActiveBusinessId] = useState<string | null>(null);

  const load = useCallback(async (signal?: { cancelled: boolean }) => {
    try {
      const me = await api.get<MeResponse>('/api/auth/me');
      if (signal?.cancelled) return;
      setUser(me.user);
      setMemberships(me.memberships);
      const stored = localStorage.getItem(BUSINESS_KEY);
      const chosen = me.memberships.find((m) => m.businessId === stored)?.businessId ?? me.memberships[0]?.businessId ?? null;
      setActiveBusinessId(chosen);
    } catch {
      if (signal?.cancelled) return;
      setUser(null);
      setMemberships([]);
      setActiveBusinessId(null);
    } finally {
      if (!signal?.cancelled) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const signal = { cancelled: false };
    void load(signal);
    return () => {
      signal.cancelled = true;
    };
  }, [load]);

  useEffect(() => {
    setActiveBusiness(activeBusinessId);
  }, [activeBusinessId]);

  const login = useCallback(
    async (email: string, password: string, remember = false, portal?: 'empresa' | 'admin') => {
      const res = await api.post<{ user: CurrentUser }>('/api/auth/login', { email, password, rememberMe: remember, portal });
      if (remember) localStorage.setItem(REMEMBER_KEY, email);
      else localStorage.removeItem(REMEMBER_KEY);
      await load();
      return res.user;
    },
    [load]
  );

  const logout = useCallback(async () => {
    await clearPushOnLogout(activeBusinessId);
    await api.post('/api/auth/logout');
    setUser(null);
    setMemberships([]);
    setActiveBusinessId(null);
    localStorage.removeItem(BUSINESS_KEY);
  }, [activeBusinessId]);

  const selectBusiness = useCallback((businessId: string) => {
    setActiveBusinessId(businessId);
    localStorage.setItem(BUSINESS_KEY, businessId);
  }, []);

  const value = useMemo<AuthValue>(() => {
    const active = memberships.find((m) => m.businessId === activeBusinessId) ?? null;
    return {
      loading,
      user,
      memberships,
      active,
      isAdmin: Boolean(user?.isPlatformAdmin),
      refresh: () => load(),
      login,
      logout,
      selectBusiness
    };
  }, [loading, user, memberships, activeBusinessId, load, login, logout, selectBusiness]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth deve ser usado dentro de AuthProvider');
  return ctx;
}

export function rememberedEmail(): string {
  return localStorage.getItem(REMEMBER_KEY) ?? '';
}

export function RequireAuth({ children, role }: { children: ReactNode; role?: 'admin' | 'business' }): JSX.Element | null {
  const { loading, user, isAdmin, active } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate(role === 'admin' ? '/gestao' : '/entrar', { replace: true });
      return;
    }
    if (role === 'admin' && !isAdmin) {
      // A conta comum não tem como chegar à administração por aqui nem sabendo o endereço.
      navigate('/entrar', { replace: true });
      return;
    }
    if (role === 'business') {
      if (isAdmin) {
        // Administração e empresa são identidades separadas: o admin gerencia licenças, não negócios.
        navigate('/admin', { replace: true });
        return;
      }
      if (!active) navigate('/precos', { replace: true });
    }
  }, [loading, user, isAdmin, active, role, navigate]);

  if (loading || !user) return null;
  if (role === 'admin' && !isAdmin) return null;
  if (role === 'business' && (isAdmin || !active)) return null;
  return <>{children}</>;
}
